import {
  OFFLINE_ADDRESS_MAX,
  type OfflineAddressDraft,
  type OfflineCheckoutDeliveryAddress,
  type OfflineCheckoutInput,
  type OfflineFulfillmentType,
} from '../types';

function trimmed(value: string | undefined, max: number): string | undefined {
  const next = value?.trim();
  if (!next) return undefined;
  return next.slice(0, max);
}

/** Empty stays omitted. Typed `0` is sent; invalid numbers are dropped. */
export function parseOptionalCoord(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const next = raw.trim();
  if (next === '') return undefined;
  const parsed = Number(next);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function pruneOfflineDeliveryAddress(
  address: OfflineCheckoutDeliveryAddress | undefined,
  customer?: { customerName?: string; phoneNumber?: string },
): OfflineCheckoutDeliveryAddress | undefined {
  const next: OfflineCheckoutDeliveryAddress = {};
  const line1 = trimmed(address?.line1, OFFLINE_ADDRESS_MAX.line1);
  const line2 = trimmed(address?.line2, OFFLINE_ADDRESS_MAX.line2);
  const city = trimmed(address?.city, OFFLINE_ADDRESS_MAX.city);
  const state = trimmed(address?.state, OFFLINE_ADDRESS_MAX.state);
  const pincode = trimmed(address?.pincode, OFFLINE_ADDRESS_MAX.pincode);
  const label = trimmed(address?.label, OFFLINE_ADDRESS_MAX.label);
  const customerName = trimmed(address?.customerName ?? customer?.customerName, 120);
  const phoneNumber = trimmed(address?.phoneNumber ?? customer?.phoneNumber, 20);

  if (line1) next.line1 = line1;
  if (line2) next.line2 = line2;
  if (city) next.city = city;
  if (state) next.state = state;
  if (pincode) next.pincode = pincode;
  if (label) next.label = label;
  if (address?.latitude !== undefined && Number.isFinite(address.latitude)) {
    next.latitude = address.latitude;
  }
  if (address?.longitude !== undefined && Number.isFinite(address.longitude)) {
    next.longitude = address.longitude;
  }
  if (customerName) next.customerName = customerName;
  if (phoneNumber) next.phoneNumber = phoneNumber;

  return Object.keys(next).length > 0 ? next : undefined;
}

export function addressDraftToCheckout(
  draft: OfflineAddressDraft,
): OfflineCheckoutDeliveryAddress | undefined {
  return pruneOfflineDeliveryAddress({
    line1: draft.line1,
    line2: draft.line2,
    city: draft.city,
    state: draft.state,
    pincode: draft.pincode,
    label: draft.label,
    latitude: parseOptionalCoord(draft.latitude),
    longitude: parseOptionalCoord(draft.longitude),
  });
}

export function formatOfflineAddressLines(
  address: OfflineCheckoutDeliveryAddress | undefined,
): string[] {
  if (!address) return [];
  const lines: string[] = [];
  if (address.label) lines.push(address.label);
  if (address.line1) lines.push(address.line1);
  if (address.line2) lines.push(address.line2);
  const locality = [address.city, address.state, address.pincode].filter(Boolean).join(', ');
  if (locality) lines.push(locality);
  return lines;
}

/**
 * In-store omits `fulfillmentType` so the payload matches the staff app.
 * Home delivery always sends `HOME_DELIVERY` and only non-empty address keys.
 */
export function buildOfflineCheckoutBody(input: OfflineCheckoutInput): Record<string, unknown> {
  const customerName = input.customerName?.trim();
  const phoneNumber = input.phoneNumber?.trim();
  const body: Record<string, unknown> = {
    storeId: input.storeId,
    idempotencyKey: input.idempotencyKey,
  };
  if (customerName) body.customerName = customerName;
  if (phoneNumber) body.phoneNumber = phoneNumber;

  if (input.fulfillmentType === 'HOME_DELIVERY') {
    body.fulfillmentType = 'HOME_DELIVERY';
    const deliveryAddress = pruneOfflineDeliveryAddress(input.deliveryAddress, {
      customerName,
      phoneNumber,
    });
    if (deliveryAddress) body.deliveryAddress = deliveryAddress;
  }

  return body;
}

export function isSuccessfulOfflineCheckout(
  status: string,
  fulfillmentType: OfflineFulfillmentType,
): boolean {
  if (fulfillmentType === 'HOME_DELIVERY') return status === 'PICKING';
  return status === 'DELIVERED';
}
